/*
 * Mock adapters for the portal contracts (types.ts).
 *
 * Same signatures the real client will have; swap the import when a backend
 * endpoint lands (Conversations goes live first — its endpoint already exists).
 * Demo data is fictional ("Meridian Bio" workspace) — never real client data.
 */
import type {
  AttendeeAdditionResult,
  NewAttendee,
  PortalWorkspace, PortalCampaign, CampaignDayPoint, CampaignLead, CampaignReply, CampaignStatsHistory, LeadList, PersonDetail, PersonLastTouch, TamFilters, TamPerson, EngagementStep,
  TodayQueueItem, TodayQueuePage, PortalIntegration, SendHistoryItem, SendHistoryPage, SendHistoryQuery, OutreachSequence, OutreachSequenceState,
  EventGoing, OutreachEnrollResult, OutreachSender, OutreachSettingsInput, OutreachSettingsResult,
} from './types';
import type { TimelineActivity } from '../../types/accounts';
import type { AccountCorrection, AccountsPage, PortalAccount, PortalAccountDetail, PortalEvent, EventsPage, EventFilters, EventSort, PortalAttendee, AttendeesPage } from './types';

const now = () => new Date().toISOString();

export const mockWorkspace: PortalWorkspace = {
  workspaceId: 'ws_demo',
  name: 'Meridian Bio',
  permissions: { approveGates: true, draftReplies: true, curateTam: true },
  sourcesBeingVerified: [],
  setupDeclined: false,
  stepsUnknown: false,
};

export const mockSteps: EngagementStep[] = [
  { key: 'intake', label: 'Intake call', status: 'done' },
  { key: 'tam', label: 'Building your target list', status: 'in_progress' },
  { key: 'first_campaign', label: 'First campaign prep', status: 'pending' },
  { key: 'campaign_live', label: 'Campaign live', status: 'pending' },
];

/** Demo campaign performance (SKY-477): a weekday-shaped send pattern with a
 * mid-window sync gap and an in-progress newest day, so the demo walkthrough
 * exercises every annotation the live chart can render. */
const demoSeries = (days: number): CampaignDayPoint[] => {
  const out: CampaignDayPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86_400_000);
    const weekday = d.getUTCDay() > 0 && d.getUTCDay() < 6;
    // Deterministic (no Math.random): the demo chart must look the same on every reload.
    const wobble = (i * 7) % 5;
    const sent = weekday ? 24 + wobble * 3 : 0;
    if (i === 3) continue; // sync gap — the next bar carries two days
    out.push({
      date: d.toISOString().slice(0, 10),
      sent: i === 2 ? sent + 26 : sent,
      opens: Math.round(sent * 0.34),
      replies: weekday && wobble > 2 ? 2 : sent > 0 ? 1 : 0,
      spannedDays: i === 2 ? 2 : null,
      // One mid-window clamped day so the "rebuilt upstream" tooltip is
      // demo-able and manually QA-able (#648 review P2).
      clamped: i === 5,
      partial: i === 0,
    });
  }
  return out;
};

/* Demo "since launch" totals = the full 90d history. The stat tiles and the
 * funnel both derive from this ONE object so no two surfaces on the campaign
 * page can contradict (metrics-audit P2 — static tiles once sat above a funnel
 * summing to 6x their value). Mirrors live semantics: tiles and funnel are
 * lifetime numbers from the latest sync; only the day-by-day chart is windowed.
 * Load-time snapshot by design: the date-anchored series drifts across UTC
 * midnight in a long-lived tab, but tile/funnel agreement is what matters. */
const demoLifetime = (() => {
  const s = demoSeries(90);
  const sum = (k: 'sent' | 'opens' | 'replies') => s.reduce((n, p) => n + p[k], 0);
  const replies = sum('replies');
  return {
    sent: sum('sent'),
    opens: sum('opens'),
    replies,
    // Derived from replies so the funnel can never render >100% conversion
    // on the meetings stage (#648 review P2).
    meetings: Math.max(1, Math.floor(replies * 0.35)),
  };
})();

export const mockCampaigns: PortalCampaign[] = [
  {
    id: 'proj_1',
    name: 'JSM Boston — biostats push',
    pipelineStage: 'live',
    status: 'active',
    needsClientReview: true,
    legs: { emailCampaignRef: 'camp_abc', event: { name: 'JSM 2026', date: '2026-08-02', kind: 'conference' } },
    stats: { ...demoLifetime },
    statsState: 'fresh',
    statsSyncedAt: now(),
    updatedAt: now(),
    audience: 'JSM attendees × your list · 412 people',
  },
  {
    id: 'proj_2',
    name: 'Boston dinner — clinical development leaders',
    pipelineStage: 'live',
    status: 'active',
    needsClientReview: false,
    legs: { event: { name: 'Private dinner', date: '2026-08-03', kind: 'dinner' } },
    stats: { sent: 0, opens: 0, replies: 6, meetings: 0, invited: 18, accepted: 6, declined: 2 },
    statsState: 'fresh',
    statsSyncedAt: now(),
    updatedAt: now(),
    audience: 'Boston metro × clinical dev · 18 seats',
  },
  {
    id: 'proj_3',
    name: 'Post-conference follow-up (Aug)',
    pipelineStage: 'copy',
    status: 'active',
    needsClientReview: false,
    legs: { event: { name: 'JSM 2026', kind: 'conference' } },
    stats: { sent: 0, opens: 0, replies: 0, meetings: 0 },
    statsState: 'fresh',
    statsSyncedAt: null,
    updatedAt: now(),
    audience: 'JSM repliers + met-in-person · 34 people',
  },
  {
    id: 'proj_4',
    name: 'Spring warm re-engage',
    pipelineStage: 'done',
    status: 'active',
    needsClientReview: false,
    legs: { linkedinSequenceRef: 'li_batch_2' },
    stats: { sent: 88, opens: 0, replies: 11, meetings: 3 },
    statsState: 'fresh',
    statsSyncedAt: now(),
    updatedAt: now(),
    audience: 'Warm network · 88 people',
  },
];

export const mockReplies: CampaignReply[] = [
  {
    id: 'r_0', provider: 'email', threadId: 't_1', direction: 'outbound',
    sender: { name: 'Your Skylarq team', title: null, company: null, entityId: null, email: null },
    subject: 'Worth 15 minutes at JSM?',
    body: "Hi Dana — we watch your market so teams act on signals, not lists. Relevant to Halcyon's biostats org — worth 15 minutes at JSM?",
    occurredAt: now(), read: true, disposition: null, triageStatus: null, mailboxEmail: null, triageInFlight: false, draft: null, fromLead: true, cc: null, bcc: null,
  },
  {
    id: 'r_1', provider: 'email', threadId: 't_1', direction: 'inbound',
    sender: { name: 'Dana Whitfield', title: 'VP Biostatistics', company: 'Halcyon Therapeutics', entityId: 'e_1', email: 'dana@halcyon.com' },
    subject: 'Re: Worth 15 minutes at JSM?',
    body: "Happy to chat — I'll be at JSM Tuesday and Wednesday. What did you have in mind?",
    // demo of the styled path (SKY-464): thread view renders this, list preview uses body
    bodyHtml: "<p>Happy to chat — I'll be at JSM <b>Tuesday</b> and <b>Wednesday</b>.</p><p>What did you have in mind?</p>",
    occurredAt: now(), read: false, disposition: null, triageStatus: null, mailboxEmail: null, triageInFlight: false, draft: null, fromLead: true, cc: null, bcc: null,
  },
  {
    id: 'r_2', provider: 'linkedin', threadId: 't_2', direction: 'inbound',
    sender: { name: 'Marcus Oyelaran', title: 'Sr Director, Clinical Dev', company: 'Corvid Bio', entityId: 'e_2', email: 'marcus@corvid.com' },
    subject: null,
    body: 'Thanks for reaching out — the dinner sounds interesting. Who else is attending?',
    occurredAt: now(), read: true, disposition: 'interested', triageStatus: null, mailboxEmail: null, triageInFlight: false, draft: null, fromLead: true, cc: null, bcc: null,
  },
];

const daysAgo = (n: number) => new Date(Date.now() - n * 24 * 60 * 60 * 1000).toISOString();

export const mockTam: TamPerson[] = [
  // Dana + Marcus have inbound replies in mockReplies — replied mirrors that.
  // Marcus is the demo's gone-quiet row (SKY-475): last touch 12 days back.
  { entityId: 'e_1', name: 'Dana Whitfield', title: 'VP Biostatistics', company: 'Halcyon Therapeutics', companyEntityId: 'acc_halcyon', email: 'dana@halcyon.com', linkedinUrl: 'https://linkedin.com/in/dana-whitfield', tier: 'T1', suppressed: false, lastSignalAt: now(), campaignIds: ['proj_1'], replied: true, addedAt: '2026-06-02T00:00:00Z', disposition: null, matchReason: 'Owns biostatistics at a mid-size sponsor — the buyer for trial analytics', provisional: false, lastTouchAt: daysAgo(1), stale: false, channels: ['email', 'linkedin'] },
  { entityId: 'e_2', name: 'Marcus Oyelaran', title: 'Sr Director, Clinical Dev', company: 'Corvid Bio', companyEntityId: null, email: 'marcus@corvid.com', linkedinUrl: null, tier: 'T1', suppressed: false, lastSignalAt: null, campaignIds: ['proj_2'], replied: true, addedAt: '2026-06-02T00:00:00Z', disposition: null, matchReason: 'Clinical development lead at a growing biotech that outsources its trials', provisional: false, lastTouchAt: daysAgo(12), stale: true, channels: ['email'] },
  { entityId: 'e_3', name: 'Priya Raghunathan', title: 'Chief Medical Officer', company: 'Atlas Oncology', companyEntityId: null, email: null, linkedinUrl: 'https://linkedin.com/in/priya-raghunathan', tier: 'T2', suppressed: false, lastSignalAt: null, campaignIds: [], replied: false, addedAt: now(), disposition: null, matchReason: 'CMO at an oncology sponsor — signs off on trial vendors', provisional: true, lastTouchAt: null, stale: false, channels: [] },
];

export const mockEvents: PortalEvent[] = [
  { id: 'cf_1', name: 'Outsourcing in Clinical Trials Europe 2026', dateStart: '2026-09-29', dateEnd: '2026-09-30', city: 'Barcelona', country: 'Spain', venue: 'Fira Barcelona', vertical: 'Clinical trials', eventType: 'conference', categories: ['Medical & Pharma', 'Science & Research'], eventFormat: 'in_person', attendeeCount: 2240, confirmedCount: 312 },
  { id: 'cf_2', name: 'Digital Pharma & DCT World 2026', dateStart: '2026-09-17', dateEnd: '2026-09-18', city: 'Amsterdam', country: 'Netherlands', venue: 'Beurs van Berlage', vertical: 'Digital pharma', eventType: 'conference', categories: ['Medical & Pharma', 'IT & Technology'], eventFormat: 'hybrid', attendeeCount: 1410, confirmedCount: 96 },
  { id: 'cf_3', name: 'BioProcess International Europe 2026', dateStart: '2026-09-30', dateEnd: '2026-10-02', city: 'Vienna', country: 'Austria', venue: 'Austria Center Vienna', vertical: 'Bioprocess', eventType: 'conference', categories: ['Medical & Pharma'], eventFormat: 'in_person', attendeeCount: 2010, confirmedCount: 149 },
];

export const mockAttendees: PortalAttendee[] = [
  { id: 'a1', fullName: 'Dana Whitfield', title: 'VP Biostatistics', orgName: 'Halcyon Therapeutics', linkedinUrl: 'https://linkedin.com/in/dana-whitfield', confidenceGrade: 'confirmed', role: 'speaker', addedByTeam: false, additionStatus: null },
  { id: 'a2', fullName: 'Priya Raghunathan', title: 'Chief Medical Officer', orgName: 'Atlas Oncology', linkedinUrl: 'https://linkedin.com/in/priya-raghunathan', confidenceGrade: 'confirmed', role: 'attendee', addedByTeam: false, additionStatus: null },
  { id: 'a3', fullName: 'Marcus Oyelaran', title: 'Sr Director, Clinical Dev', orgName: 'Corvid Bio', linkedinUrl: null, confidenceGrade: 'likely', role: 'attendee', addedByTeam: false, additionStatus: null },
  { id: 'a4', fullName: 'Sam Rivera', title: 'Head of Ops', orgName: 'Rivera Labs', linkedinUrl: null, confidenceGrade: null, role: 'attendee', addedByTeam: true, additionStatus: 'pending' },
];

export const mockLeadLists: LeadList[] = [
  { id: 'list_1', name: 'JSM attendees · June scrape', kind: 'scrape', createdAt: daysAgo(30), gtmProjectId: 'proj_1', memberCount: 2, keptCount: 0, cutCount: 0, undecidedCount: 2, approvedAt: null },
  { id: 'list_2', name: 'Warm intros upload', kind: 'upload', createdAt: daysAgo(14), gtmProjectId: null, memberCount: 1, keptCount: 0, cutCount: 0, undecidedCount: 1, approvedAt: null },
];
// list → member entityIds (demo mirror of lead_list_members)
const mockListMembers: Record<string, string[]> = {
  list_1: ['e_1', 'e_2'],
  list_2: ['e_3'],
};

export const mockTodayQueue: TodayQueueItem[] = [
  { id: 'q1', campaignId: 'proj_1', campaignName: 'JSM Boston — biostats push', channel: 'linkedin', person: 'Priya Raghavan', title: 'Senior Director, Clinical Ops', company: 'Genentech', nextStep: 'invite', reason: 'first touch', status: 'scheduled', inCapacity: true },
  { id: 'q2', campaignId: 'proj_1', campaignName: 'JSM Boston — biostats push', channel: 'linkedin', person: 'Jonas Lindqvist', title: 'Principal Scientist', company: 'Vertex', nextStep: 'invite', reason: 'first touch', status: 'scheduled', inCapacity: false },
  { id: 'q3', campaignId: 'proj_1', campaignName: 'JSM Boston — biostats push', channel: 'email', person: 'Sarah Kwon', title: 'Director, Biostatistics', company: 'Merck', nextStep: 'follow_up', reason: 'email 2 — no reply after 4 days', status: 'scheduled', inCapacity: true },
  { id: 'q4', campaignId: 'proj_1', campaignName: 'JSM Boston — biostats push', channel: 'email', person: 'Michael Fries', title: 'VP Clinical Development', company: 'Amgen', nextStep: 'first_touch', reason: 'no LinkedIn accept after 5 days', status: 'held', inCapacity: true },
  { id: 'q5', campaignId: 'proj_2', campaignName: 'Boston dinner — clinical development leaders', channel: 'email', person: 'Daniel Reyes', title: 'VP Clinical Ops', company: 'Vertex', nextStep: 'first_touch', reason: 'first touch', status: 'scheduled', inCapacity: true },
];

// LinkedIn left unconnected so demo walkthroughs show the finish-setup ask
// and connect button (SKY-460) without hitting the real endpoints.
export const mockIntegrations: PortalIntegration[] = [
  { provider: 'gmail', name: 'Gmail', connected: true, account: 'outreach@meridianbio.com', status: 'connected', connectedAt: now() },
  { provider: 'google_calendar', name: 'Google Calendar', connected: true, account: 'outreach@meridianbio.com', status: 'connected', connectedAt: now() },
  // One expired connection on purpose (same intent as demoSeries above): the
  // walkthrough should exercise the needs-you row, not only the happy path.
  { provider: 'slack', name: 'Slack', connected: true, account: 'Meridian Bio', status: 'expired', connectedAt: now() },
  { provider: 'linkedin', name: 'LinkedIn', connected: false, account: null, status: 'not_connected', connectedAt: null },
  { provider: 'instantly', name: 'Instantly', connected: false, account: null, status: 'not_connected', connectedAt: null },
  { provider: 'hubspot', name: 'HubSpot', connected: false, account: null, status: 'not_connected', connectedAt: null },
  { provider: 'granola', name: 'Granola', connected: true, account: null, status: 'connected', connectedAt: null, lastSyncedAt: new Date(Date.now() - 2 * 86_400_000).toISOString() },
];

export const mockStatsHistory = (days: number): CampaignStatsHistory => ({
  state: 'fresh',
  // Funnel is "since launch" and does NOT respond to the window pills — same as
  // live, where it reads the latest snapshot's lifetime counters. Only the
  // day-by-day series is windowed. (Stacked-review P1s: windowed funnels put
  // zeros above non-zeros and mislabel lifetime totals as window activity.)
  funnel: {
    leads: 412,
    contacted: 386, // unique people in-sequence since launch — never exceeds the list
    ...demoLifetime,
    bounced: 9,
  },
  series: demoSeries(days),
  days,
});

/** SKY-533/534 demo roster + timelines — fixture-mirrored (linkedin_roster_page
 * .example.json shapes), attached to the demo LinkedIn campaign (proj_4). */
const mockLinkedInLeads: import('./types').LinkedInRosterLead[] = [
  { leadId: 'li_1', linkedinUrl: 'https://linkedin.com/in/jane-doe', name: 'Jane Doe', company: 'Acme Health', title: 'VP RevCycle', stage: 'REPLIED', personalizationLine: 'loved your HFMA panel takeaways', variant: 'b', entityId: 'e_1', lastEventType: 'reply_received', lastStep: null, lastAt: daysAgo(2) },
  { leadId: 'li_2', linkedinUrl: 'https://linkedin.com/in/sam-lee', name: 'Sam Lee', company: 'Northwind Clinics', title: 'CFO', stage: 'MSG2', personalizationLine: null, variant: 'a', entityId: null, lastEventType: 'message_sent', lastStep: 2, lastAt: daysAgo(5) },
  { leadId: 'li_3', linkedinUrl: 'https://linkedin.com/in/pat-kim', name: 'Pat Kim', company: 'Cedar Medical', title: 'Director of Finance', stage: 'INVITED', personalizationLine: null, variant: 'a', entityId: null, lastEventType: 'invite_sent', lastStep: null, lastAt: daysAgo(1) },
  { leadId: 'li_4', linkedinUrl: 'https://linkedin.com/in/lee-osei', name: 'Lee Osei', company: 'Halcyon Therapeutics', title: 'VP Ops', stage: 'ALREADY_CONNECTED', personalizationLine: null, variant: null, entityId: null, lastEventType: null, lastStep: null, lastAt: null },
];

const mockLinkedInTimelines: Record<string, import('./types').LinkedInTimelineEvent[]> = {
  li_1: [
    { type: 'invite_sent', at: daysAgo(20), step: null, text: null, templateKey: null, templateInferred: false },
    { type: 'invite_accepted', at: daysAgo(15), step: null, text: null, templateKey: null, templateInferred: false },
    { type: 'message_sent', at: daysAgo(14), step: 1, text: 'Hey Jane, thanks for connecting! Loved your HFMA panel takeaways.', templateKey: 'msg1', templateInferred: true },
    { type: 'message_sent', at: daysAgo(9), step: 2, text: 'Quick follow-up — worth 15 minutes on revcycle automation?', templateKey: 'msg2', templateInferred: true },
    { type: 'reply_received', at: daysAgo(2), step: null, text: null, templateKey: null, templateInferred: false },
  ],
  li_2: [
    { type: 'invite_sent', at: daysAgo(12), step: null, text: null, templateKey: null, templateInferred: false },
    { type: 'invite_accepted', at: daysAgo(9), step: null, text: null, templateKey: null, templateInferred: false },
    // text null on purpose: the honest "no template resolved" state (SKY-532)
    { type: 'message_sent', at: daysAgo(8), step: 1, text: null, templateKey: null, templateInferred: false },
    { type: 'message_sent', at: daysAgo(5), step: 2, text: null, templateKey: null, templateInferred: false },
  ],
  li_3: [
    { type: 'invite_sent', at: daysAgo(1), step: null, text: null, templateKey: null, templateInferred: false },
  ],
  li_4: [],
};

export const mockOutreachSequence: OutreachSequence = {
  id: 'seq_demo',
  status: 'active',
  mode: 'simulated',
  timezone: 'America/New_York',
  windowStart: '09:00',
  windowEnd: '17:00',
  dailyCap: 30,
  senderUserId: 'user_demo_1',
  sendWeekdaysOnly: true,
  steps: [
    { id: 'st_1', position: 0, channel: 'email', delayDays: 0, subject: 'Prior auth at {{company}}', body: 'Hi {{first_name | there}},\n\n{{personalization}} Most rheum practices we talk to lose a day a week to prior auth follow-up. We cut that to an hour at two groups your size.\n\nWorth a 15-minute look?', leadCopyCount: 12, notBefore: null, pendingChange: null, pendingAt: null },
    { id: 'st_2', position: 1, channel: 'email', delayDays: 3, subject: 'Re: Prior auth at {{company}}', body: 'Hi {{first_name}}, following up on my note. Happy to send the two case studies if that is easier.', leadCopyCount: 0, notBefore: null, pendingChange: null, pendingAt: null },
    { id: 'st_3', position: 2, channel: 'email', delayDays: 5, subject: 'Closing the loop', body: 'Last note from me, {{first_name}}. If timing is off, I will check back next quarter.', leadCopyCount: 0, notBefore: null, pendingChange: null, pendingAt: null },
  ],
  enrollmentCounts: { active: 42, paused: 1, replied: 3, finished: 7, stopped: 3, unknown: 0 },
  liveReach: 42,
  writtenEmailTracks: 3,
  stoppedReasons: { no_email_address: 1, company_replied: 2 },
  heldReasons: { 'missing_variable:personalization': 2 },
  pausedReasons: { auto_reply: 1 },
  finishedReasons: {},
  failedReasons: { reconnect: 2 },
  variables: ['first_name', 'last_name', 'name', 'email', 'company', 'title', 'linkedin_url', 'phone', 'personalization', 'signature', 'unsubscribe_link', 'specialty'],
  appendSignature: true,
  signatureStatus: 'fetched',
  editable: true,
  lastSendOn: null,
  noHardStop: false,
  hardStopRequired: false,
  appRequests: false,
  variableValues: {},
  warnings: [],
};

const mockOutreachSenders: OutreachSender[] = [
  { userId: 'user_demo_1', name: 'Dana Whitfield', email: 'dana@meridianbio.example', gmailConnected: true, linkedinConnected: true },
  { userId: 'user_demo_2', name: 'Sam Ortiz', email: 'sam@meridianbio.example', gmailConnected: false, linkedinConnected: true },
];

export const portalApi = {
  getOutreachSequence: async (): Promise<OutreachSequenceState> =>
    ({ engine: 'temporal', canOperate: false, sequence: mockOutreachSequence, needsAttention: null }),
  getOutreachSenders: async (): Promise<OutreachSender[]> => mockOutreachSenders,
  updateOutreachSettings: async (input: OutreachSettingsInput): Promise<OutreachSettingsResult> => ({
    sequence: { ...mockOutreachSequence, ...input },
    warnings: (input.dailyCap ?? 0) > 30 ? ["A daily cap above 30 risks the sender's deliverability"] : [],
  }),
  enrollApprovedLeads: async (): Promise<OutreachEnrollResult> => ({
    enrolled: 18,
    skipped: [{ entityId: 'ent_1', reason: 'already_enrolled' }, { entityId: 'ent_2', reason: 'no_email' }],
  }),
  getWorkspace: async (): Promise<PortalWorkspace> => mockWorkspace,
  getCampaignStatsHistory: async (campaignId: string, days = 30): Promise<CampaignStatsHistory> => {
    void campaignId; // real adapter passes this to GET /portal/campaigns/{id}/stats-history
    return mockStatsHistory(days);
  },
  getIntegrations: async (): Promise<PortalIntegration[]> => mockIntegrations,
  getNotificationSettings: async (): Promise<import('./types').NotificationSettings> => ({
    dailyBrief: true,
    replyAlerts: true,
    progress: true,
    email: false,
    slackConnected: true,
  }),
  getTodayQueue: async (): Promise<TodayQueuePage> => ({
    state: 'fresh',
    rows: mockTodayQueue,
    truncated: false,
  }),
  getEngagementSteps: async (): Promise<EngagementStep[]> => mockSteps,
  getEvents: async (
    q?: string,
    sort: EventSort = 'date',
    page = 1,
    filters: EventFilters = {},
  ): Promise<EventsPage> => {
    void page;
    const needle = q?.trim().toLowerCase();
    let items = needle ? mockEvents.filter((c) => c.name.toLowerCase().includes(needle)) : mockEvents;
    if (filters.kind) items = items.filter((c) => c.eventType === filters.kind);
    if (filters.format) items = items.filter((c) => c.eventFormat === filters.format);
    if (filters.category) items = items.filter((c) => c.categories?.includes(filters.category as string));
    const sorted = [...items].sort((a, b) => {
      if (sort === 'size') return (b.attendeeCount ?? 0) - (a.attendeeCount ?? 0);
      if (sort === 'name') return a.name.localeCompare(b.name);
      // undated last, matching the endpoint's Postgres ASC NULLS LAST
      return (a.dateStart ?? '9999-12-31').localeCompare(b.dateStart ?? '9999-12-31');
    });
    return { items: sorted, total: sorted.length };
  },
  getEvent: async (id: string): Promise<PortalEvent | null> => mockEvents.find((e) => e.id === id) ?? null,
  getEventAttendees: async (id: string, page = 1): Promise<AttendeesPage> => {
    void id;
    void page;
    return { items: mockAttendees, total: mockAttendees.length };
  },
  getCampaigns: async (): Promise<PortalCampaign[]> => mockCampaigns,
  getCampaign: async (id: string): Promise<PortalCampaign | null> => mockCampaigns.find((c) => c.id === id) ?? null,
  uploadEventAttendees: async (eventId: string, file: File): Promise<AttendeeAdditionResult> => {
    void eventId;
    return { batchId: `demo-${file.name}`, added: 2, alreadyFound: 1, rejected: 0, rejects: [] };
  },

  addEventAttendee: async (eventId: string, person: NewAttendee): Promise<AttendeeAdditionResult> => {
    void eventId;
    return { batchId: `demo-${person.fullName}`, added: 1, alreadyFound: 0, rejected: 0, rejects: [] };
  },

  undoEventAttendeeBatch: async (eventId: string, batchId: string): Promise<number> => {
    void eventId;
    return batchId ? 1 : 0;
  },

  markEventGoing: async (
    eventId: string,
    notes?: string,
    offer?: string,
  ): Promise<EventGoing> => {
    void eventId; // real adapter posts to /portal/events/{id}/going
    return {
      projectId: mockCampaigns[0].id,
      drafting: Boolean(notes?.trim() || offer?.trim()),
    };
  },
  getReplies: async (campaignId: string): Promise<CampaignReply[]> => {
    void campaignId; // real adapter passes this to GET /pipeline/projects/{id}/replies
    return mockReplies;
  },
  // Mirrors the server-side filter semantics (SKY-456) so demo mode exercises
  // the same behavior the live endpoint has.
  getTam: async (filters?: TamFilters): Promise<TamPerson[]> => {
    const q = filters?.q?.trim().toLowerCase();
    const newCutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return mockTam.filter((p) => {
      if (q && !`${p.name} ${p.company ?? ''}`.toLowerCase().includes(q)) return false;
      if (filters?.status === 'suppressed' && !p.suppressed) return false;
      if (filters?.status === 'new' && !(p.addedAt && new Date(p.addedAt).getTime() >= newCutoff)) return false;
      if (filters?.replied && !p.replied) return false;
      if (filters?.stale && !p.stale) return false;
      if (filters?.listId && !(mockListMembers[filters.listId] ?? []).includes(p.entityId)) return false;
      if (filters?.tier && p.tier !== filters.tier) return false;
      return true;
    });
  },
  // SKY-475 person-panel block — mirrors the live shape (Marcus is the stale demo row).
  // Demo ledger (SKY-498) — mirrors the live filter semantics so the walkthrough
  // exercises the same behavior; single page, cursor always exhausted.
  getSendHistory: async (query: SendHistoryQuery = {}): Promise<SendHistoryPage> => {
    const rows: SendHistoryItem[] = [
      {
        id: 'sh_1', channel: 'email', occurredAt: daysAgo(0.2), recipient: 'marcus@corvidbio.example',
        displayName: 'Marcus Oyelaran', title: 'Sr Director, Clinical Dev', company: 'Corvid Bio',
        entityId: 'e_2', projectId: 'proj_2', campaignName: 'Boston dinner — clinical development leaders',
        stepIndex: null, nextStep: 'first_touch', mailboxEmail: null, outcome: 'held', repliedAt: null,
        reason: 'Board asked to pause Corvid outreach', threadId: null, subject: null, body: null,
      },
      {
        id: 'sh_2', channel: 'email', occurredAt: daysAgo(1), recipient: 'dana@halcyon.example',
        displayName: 'Dana Whitfield', title: 'VP Biostatistics', company: 'Halcyon Therapeutics',
        entityId: 'e_1', projectId: 'proj_1', campaignName: 'JSM Boston — biostats push',
        stepIndex: 1, nextStep: null, mailboxEmail: 'outreach@meridianbio.example',
        outcome: 'replied', repliedAt: daysAgo(0.8), reason: null, threadId: 't_1',
        subject: 'Dinner during JSM?', body: 'Dana — following up on the biostats dinner…',
      },
      {
        // LinkedIn message send (SKY-566): no mailbox; body = template text.
        id: 'sh_li_1', channel: 'linkedin', occurredAt: daysAgo(1.5),
        recipient: 'https://www.linkedin.com/in/priya-raghavan-example',
        displayName: 'Priya Raghavan', title: 'Senior Director, Clinical Ops', company: 'Genentech',
        entityId: null, projectId: 'proj_1', campaignName: 'JSM Boston — biostats push',
        stepIndex: 1, nextStep: null, mailboxEmail: null, outcome: 'sent', repliedAt: null,
        reason: null, threadId: null, subject: 'Message 1',
        body: 'Priya — great connecting. We are hosting a biostats dinner at JSM…',
      },
      {
        // LinkedIn connection invite — production shape per the projection:
        // stepIndex 0, synthetic subject, body ALWAYS null (no template path
        // for invites). Exercises the 'Connection invite' label and the
        // non-expandable-row branch the dev walk must verify.
        id: 'sh_li_2', channel: 'linkedin', occurredAt: daysAgo(1.7),
        recipient: 'https://www.linkedin.com/in/priya-raghavan-example',
        displayName: 'Priya Raghavan', title: 'Senior Director, Clinical Ops', company: 'Genentech',
        entityId: null, projectId: 'proj_1', campaignName: 'JSM Boston — biostats push',
        stepIndex: 0, nextStep: null, mailboxEmail: null, outcome: 'sent', repliedAt: null,
        reason: null, threadId: null, subject: 'Connection invite', body: null,
      },
      {
        id: 'sh_3', channel: 'email', occurredAt: daysAgo(2), recipient: 'priya@atlasonc.example',
        displayName: 'Priya Raghunathan', title: 'Chief Medical Officer', company: 'Atlas Oncology',
        entityId: null, projectId: 'proj_1', campaignName: 'JSM Boston — biostats push',
        stepIndex: 0, nextStep: null, mailboxEmail: 'outreach@meridianbio.example',
        outcome: 'sent', repliedAt: null, reason: null, threadId: 't_9',
        subject: 'Biostats dinner at JSM', body: 'Priya — we are hosting a small dinner…',
      },
      {
        id: 'sh_4', channel: 'email', occurredAt: daysAgo(3), recipient: 'ken@atlasonc.example',
        displayName: 'Ken Aldana', title: 'Director Quantitative Sciences', company: 'Atlas Oncology',
        entityId: null, projectId: 'proj_1', campaignName: 'JSM Boston — biostats push',
        stepIndex: null, nextStep: 'first_touch', mailboxEmail: null, outcome: 'skipped', repliedAt: null,
        reason: 'You messaged him from your own inbox this week', threadId: null, subject: null, body: null,
      },
    ];
    const q = query.q?.trim().toLowerCase();
    return {
      asOf: now(),
      state: 'fresh',
      nextBefore: null,
      rows: rows.filter((r) => {
        if (query.projectId && r.projectId !== query.projectId) return false;
        if (query.outcome && query.outcome !== 'any') {
          if (query.outcome === 'sent' ? r.outcome === 'held' || r.outcome === 'skipped' : r.outcome !== query.outcome) return false;
        }
        if (q && !(r.recipient ?? '').includes(q)) return false;
        if (query.mailbox && r.mailboxEmail !== query.mailbox) return false;
        if (query.since && (r.occurredAt ?? '') < query.since) return false;
        return true;
      }).slice(0, query.limit ?? 50),
    };
  },

  // SKY-513 demo person page: built from the demo world's TAM rows; Halcyon
  // people link to the demo account. Unknown fields stay null (honest absent).
  getPersonDetail: async (entityId: string): Promise<PersonDetail | null> => {
    const p = mockTam.find((t) => t.entityId === entityId);
    if (!p) return null;
    const halcyon = p.company === 'Halcyon Therapeutics';
    return {
      entityId: p.entityId,
      name: p.name,
      title: p.title,
      company: p.company,
      companyEntityId: halcyon ? 'acc_halcyon' : null,
      location: halcyon ? 'Boston, MA' : null,
      tier: p.tier,
      suppressed: p.suppressed,
      replied: p.replied,
      provisional: p.provisional,
      addedAt: p.addedAt,
      email: p.entityId === 'e_1' ? 'dana@halcyon.com' : null,
      linkedinUrl: p.entityId === 'e_1' ? 'https://linkedin.com/in/dana-whitfield' : null,
      colleagues: mockTam
        .filter((o) => o.company && o.company === p.company && o.entityId !== p.entityId)
        .map((o) => ({ entityId: o.entityId, name: o.name, title: o.title })),
    };
  },

  getPersonLastTouch: async (entityId: string): Promise<PersonLastTouch> => {
    const p = mockTam.find((t) => t.entityId === entityId);
    // A reply halts the sequence (nothing queued); otherwise a touched, non-stale
    // person has a follow-up queued in two days.
    const queued = !!p && !p.replied && !!p.lastTouchAt && !p.stale;
    return {
      lastTouchAt: p?.lastTouchAt ?? null,
      stale: p?.stale ?? false,
      nextSendDueOn: queued ? daysAgo(-2).slice(0, 10) : null,
      nextSendStep: queued ? 'follow_up' : null,
      stoppedOnReply: !queued && (p?.replied ?? false),
    };
  },
  // SKY-478 demo accounts: derived from the Meridian Bio demo world's people.
  getAccounts: async (q?: string): Promise<AccountsPage> => {
    const rows: PortalAccount[] = [
      {
        entityId: 'acc_halcyon',
        name: 'Halcyon Therapeutics',
        domain: 'halcyontx.example',
        peopleCount: 2,
        replyingCount: 1,
        lastTouchAt: daysAgo(1),
      },
    ];
    const term = (q ?? '').trim().toLowerCase();
    const filtered = term
      ? rows.filter((r) => r.name.toLowerCase().includes(term) || (r.domain ?? '').includes(term))
      : rows;
    return { total: filtered.length, accounts: filtered };
  },
  getAccount: async (entityId: string): Promise<PortalAccountDetail> => {
    const [account] = (await portalApi.getAccounts()).accounts;
    return {
      ...account,
      entityId,
      stage: 'replied',
      hasBrief: true,
      briefUpdatedAt: daysAgo(1),
      claims: [
        {
          section: 'Overview',
          text: 'Runs two Phase II oncology trials with adaptive designs; biostatistics led in-house under Dana Whitfield.',
          sources: [{ label: 'meeting', date: daysAgo(14).slice(0, 10) }],
        },
        {
          section: 'Key Topics & Signals',
          text: 'Attending JSM 2026 with a poster on group-sequential methods (Dana presenting).',
          sources: [{ label: 'email', date: daysAgo(16).slice(0, 10) }],
        },
      ],
    };
  },
  // Demo world has no stored corrections; signature mirrors the live adapter.
  getAccountCorrections: async (entityId: string): Promise<AccountCorrection[]> => {
    void entityId;
    return [];
  },
  getEntityActivityFeed: async (entityId: string): Promise<TimelineActivity[]> => {
    // Person id → that person; the demo account id → its warmest person, so the
    // account page's Company feed agrees with its People section.
    const p =
      mockTam.find((t) => t.entityId === entityId) ??
      (entityId === 'acc_halcyon'
        ? mockTam.find((t) => t.company === 'Halcyon Therapeutics' && t.replied)
        : undefined);
    if (!p?.lastTouchAt) return [];
    return [
      {
        id: `demo-act-${entityId}`,
        activity_type: p.replied ? 'reply_received' : 'email_sent',
        activity_category: null,
        source_type: 'email',
        occurred_at: p.lastTouchAt,
        label: p.replied ? `${p.name} replied` : `Email to ${p.name}`,
      },
    ];
  },
  getLeadLists: async (): Promise<LeadList[]> => mockLeadLists,
  // Demo audience derives from the TAM people already tagged with the campaign
  // (emails are fictional, matching the Meridian Bio demo workspace).
  getLinkedInSequence: async (campaignId: string): Promise<import('./types').LinkedInSequence | null> => {
    if (campaignId !== 'proj_4') return null;
    return {
      accountId: 'li:demo',
      capturedAt: now(),
      steps: [
        { step: 0, label: 'invite', stepDelayDays: null, inferred: false, variants: [{ key: 'invite_note', text: null }] },
        { step: 1, label: 'msg1', stepDelayDays: null, inferred: true, variants: [
          { key: 'msg1_no_company', text: "Hey {first}, thanks for connecting! Saw what you're building and thought I'd reach out." },
          { key: 'msg1_with_company', text: 'Hey {first}, thanks for connecting! Loved what {company} is doing.' },
        ] },
        { step: 2, label: 'msg2', stepDelayDays: null, inferred: true, variants: [{ key: 'msg2', text: 'one thing that might actually be useful — we just ran a similar play for a team like yours.' }] },
        // The SKY-532 uncaptured state stays demo-able.
        { step: 3, label: 'msg3', stepDelayDays: null, inferred: false, variants: [{ key: 'msg3', text: null }] },
      ],
    };
  },
  getLinkedInReport: async (campaignId: string, days = 30): Promise<import('./types').LinkedInReport> => {
    if (campaignId !== 'proj_4') return { state: 'no_campaign', accountId: null, funnel: null, series: [], days, syncedAt: null };
    // Deterministic demo series with one gap-carrying bar, one clamped day and
    // a partial today — every annotation the live chart can render.
    const series: import('./types').LinkedInReportDay[] = [];
    for (let i = 13; i >= 0; i--) {
      if (i === 4) continue; // sync gap — the next bar carries two days
      const d = new Date(Date.now() - i * 86_400_000);
      const w = (i * 5) % 4;
      series.push({
        date: d.toISOString().slice(0, 10),
        invited: 2 + w, accepted: w > 1 ? 2 : 1, messaged: 3 + ((i * 3) % 4), replied: w === 3 ? 1 : 0,
        clamped: i === 7, spannedDays: i === 3 ? 2 : null, partial: i === 0,
      });
    }
    return { state: 'fresh', accountId: 'li:demo', funnel: { invited: 96, accepted: 41, messaged: 88, replied: 4 }, series, days, syncedAt: now() };
  },
  getLinkedInLeads: async (campaignId: string, offset = 0): Promise<import('./types').LinkedInLeadsPage> => {
    // Only the demo LinkedIn campaign has a roster; others render the honest
    // unstamped empty state (account_id null — the SKY-531 case).
    if (campaignId !== 'proj_4') return { accountId: null, total: 0, limit: 100, offset: 0, leads: [] };
    return { accountId: 'li:demo', total: mockLinkedInLeads.length, limit: 100, offset, leads: offset === 0 ? mockLinkedInLeads : [] };
  },
  getLinkedInLeadTimeline: async (leadId: string): Promise<import('./types').LinkedInLeadTimeline> => {
    const lead = mockLinkedInLeads.find((l) => l.leadId === leadId);
    return {
      leadId, accountId: 'li:demo',
      linkedinUrl: lead?.linkedinUrl ?? null, name: lead?.name ?? null, company: lead?.company ?? null,
      title: lead?.title ?? null, stage: lead?.stage ?? 'NEW', entityId: lead?.entityId ?? null,
      personalizationLine: lead?.personalizationLine ?? null, variant: lead?.variant ?? null, segment: null,
      events: mockLinkedInTimelines[leadId] ?? [],
    };
  },
  getCampaignLeads: async (campaignId: string): Promise<CampaignLead[]> =>
    mockTam
      .filter((p) => p.campaignIds.includes(campaignId))
      .map((p) => ({
        email: `${p.name.split(' ')[0].toLowerCase()}@${(p.company ?? 'example').split(' ')[0].toLowerCase()}.com`,
        name: p.name,
        company: p.company,
        title: p.title,
        entityId: p.entityId,
      })),
  // Demo resolve-first (SKY-467): match the generated demo email back to its person.
  resolveLeadEntity: async (email: string): Promise<string | null> =>
    mockTam.find(
      (p) =>
        `${p.name.split(' ')[0].toLowerCase()}@${(p.company ?? 'example').split(' ')[0].toLowerCase()}.com` === email,
    )?.entityId ?? null,
};
